import DialogContent from "@mui/material/DialogContent";
import {
  BulkActionType,
  IBulkEventPayload,
  IBulkOpsState,
  IDelayCategory,
  IFlight,
  IGroundEvent,
} from "@vmosolutions/oc-interfaces";
import React, { useCallback, useEffect, useState } from "react";

import { bulkEvent } from "src/redux/actions/flights/FlightData.Actions";
import { LoadingMessage } from "../../LoadingMessage/LoadingMessage";

import { DialogActions, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { ModalButton, ModalErrorText, ModalTitle } from "../DialogModalComponents";
import { EventModalTypes } from "../DialogModalWrapper";
// form components
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";
import { DialogModalStyles } from "../DialogModalWrapper";
// Remove open editor windows if events are deleted
import { removeEditFlightWindow, setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";

/**
 * @param isOpen - to keep track of whither the modal should be open or closed
 * @param handleClose - sets isOpen to true or false in DialogModalWrapper.tsx
 * @param setActiveFlightModal - sets the  state of activeFlightModal in FlightTimelineWrapper.tsx
 * @returns the modal
 */

interface ICancelEventModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

export const CancelEventModal = (props: ICancelEventModalProps): JSX.Element => {
  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );
  const structures = useSelector((state: IRootState) => state.structures.customerStructure);
  const theme = useTheme();
  // For BULK batch tracking
  const [batchId, setBatchId] = useState<any>(null);
  const batchJob: IBulkOpsState = useSelector((state: IRootState) => state.bulkBatch[batchId]);
  const flightCancellationReasons = useSelector((state: IRootState) => state.UISettings.flightCancellationReasons);
  const [building, setBuilding] = useState<boolean>(false);
  const [buildStatus, setBuildStatus] = useState<string>(null);

  const [cancelReason, setCancelReason] = useState<any>(null);
  const [cancelComment, setCancelComment] = useState<any>(null);

  //error checking
  const [textError, setTextError] = useState<string>(null);
  const [reasonError, setReasonError] = useState<any>(null);
  const dispatch = useDispatch();

  const diversionTitles = useCallback(() => {
    if (flightCancellationReasons.length === 0) {
      return structures.delayCategories
        .map((opt: IDelayCategory) => {
          return {
            value: opt._id,
            label: opt.description,
          };
        })
        .sort((a, b) => {
          return a.label.toLowerCase().localeCompare(b.label?.toLowerCase());
        });
    } else {
      return flightCancellationReasons
        .map((str: string) => {
          return {
            value: str,
            label: str,
          };
        })
        .sort((a, b) => {
          return a.label.toLowerCase().localeCompare(b.label?.toLowerCase());
        });
    }
  }, [flightCancellationReasons, structures.delayCategories]);
  // sort options alphabetically.;

  const selectedGroundEvents: IGroundEvent[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => !isFlight(event)) as IGroundEvent[];
  }, [selectedEvents]);

  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  // get type of event
  const typeFlight = selectedFlights.length > 0;
  const groundEventPluralFormat = selectedGroundEvents.length > 1 ? "Ground Events" : "Ground Event";
  const flightPluralFormat = selectedFlights.length > 1 ? "Flights" : "Flight";

  useEffect(() => {
    if (!batchJob) return;

    const eventTypes = typeFlight ? "flights" : "ground events";
    const { records, successfulEventIds, failedEvents } = batchJob;

    const failedUpdates = failedEvents?.length > 0 ? `, ${failedEvents?.length} ${eventTypes} failed to cancel` : "";
    const statusMessage = `${successfulEventIds?.length} of ${records} ${eventTypes} successfully canceled${failedUpdates}`;

    if (buildStatus !== statusMessage) {
      setBuildStatus(statusMessage);
    }

    const allProcessed = successfulEventIds?.length + failedEvents?.length === records;
    if (allProcessed) {
      const failedEventsList = typeFlight
        ? selectedFlights.filter((flight) => !successfulEventIds.map((m) => m.objectId).includes(flight._id))
        : selectedGroundEvents.filter((ge) => !successfulEventIds.map((m) => m.objectId).includes(ge._id));

      if (failedEventsList.length > 0) {
        dispatch(setSelectedEvents(failedEventsList));
      }

      if (successfulEventIds?.length === records) {
        props.handleClose(false);
        props.setActiveEventModal(EventModalTypes.None);
      }

      setBuilding(false);
    }
  }, [batchJob, buildStatus, dispatch, props, selectedFlights, selectedGroundEvents, typeFlight]);

  //////////////
  // HANDLERS //
  //////////////

  const getTitle = (): string => {
    if (typeFlight) {
      return `Cancel ${flightPluralFormat}`;
    } else {
      return `Cancel ${groundEventPluralFormat}`;
    }
  };

  const nativeFormHandler = (event: any): void => {
    setCancelReason(event);
    setReasonError(null);
  };

  const textFieldHandler = (event: any): void => {
    setCancelComment(event.target.value);
    setTextError(null);
  };

  const handleCancel = useCallback((): void => {
    // If a bulk operation was started and users choose to close the modal, clear all selected events making it easier for them to start a new bulk operation.
    if (buildStatus) dispatch(setSelectedEvents([]));
    props.handleClose(false);
    props.setActiveEventModal(EventModalTypes.None);
  }, [buildStatus, dispatch, props]);

  const handleConfirm = useCallback(async (): Promise<void> => {
    if (!cancelComment || !cancelReason) return;

    const isBuilding = building || !!buildStatus;
    if (isBuilding) return; // Prevent repeated state changes while processing

    setBuilding(true);
    setBuildStatus(typeFlight ? "Canceling selected flights" : "Canceling selected ground events");

    const selectedIds = typeFlight
      ? selectedFlights.map((flight) => {
          dispatch(removeEditFlightWindow(flight._id));
          return flight._id;
        })
      : selectedGroundEvents.map((event) => {
          dispatch(removeEditFlightWindow(event._id));
          return event._id;
        });

    const bulkPayload: IBulkEventPayload = {
      actionType: typeFlight ? BulkActionType.CANCEL_FLIGHT : BulkActionType.CANCEL_GROUND_EVENT,
      batchIds: selectedIds,
      payload: {
        canceled: true,
        reason: cancelReason.label,
        description: cancelComment,
      },
    };

    const newBatchId = await dispatch(bulkEvent(bulkPayload, selectedIds.length));
    setBatchId(newBatchId);
  }, [building, buildStatus, cancelComment, cancelReason, dispatch, selectedFlights, selectedGroundEvents, typeFlight]);
  //////////////
  //  Return  //
  //////////////

  if (!props.isOpen) {
    return null;
  }

  return (
    <DialogContent sx={DialogModalStyles(theme)}>
      <div className="modal_content" style={{ minWidth: "400px" }}>
        {!!typeFlight && <FlightChipCollection flights={selectedFlights} disableContext={true} disableFocus={true} />}

        <ModalTitle title={getTitle()}></ModalTitle>

        <SelectField
          className="modal_selection"
          label="Reason"
          name="cancelReason"
          value={cancelReason}
          onChange={nativeFormHandler}
          options={diversionTitles()}
          isSearchable={true}
          isClearable={true}
          menuPosition="fixed"
          menuPlacement="auto"
          maxMenuHeight={200}
          openMenuOnFocus={false}
          placeholder="Select Reason..."
        />

        {!!reasonError && <ModalErrorText error={reasonError}></ModalErrorText>}

        <TextField
          className="modalTextfield"
          label="Comments"
          name="cancelEventComments"
          value={cancelComment || ""}
          onChange={(e) => {
            if (cancelComment !== e.target.value) {
              setCancelComment(e.target.value);
              setTextError(null);
            }
          }}
          placeholder="Comments..."
          multiline={true}
          minRows={2}
          maxRows={4}
        />

        {!!textError && <ModalErrorText error={textError}></ModalErrorText>}

        <DialogActions>
          <ModalButton label={buildStatus ? "Close" : "Cancel"} handler={handleCancel}></ModalButton>
          <ModalButton
            label="Confirm"
            disable={building || !!buildStatus || !cancelReason || !cancelComment}
            handler={handleConfirm}
          ></ModalButton>
        </DialogActions>

        <div className="modal_status">
          {!!buildStatus && <LoadingMessage message={buildStatus} animate={building} />}
        </div>
      </div>
    </DialogContent>
  );
};
